import React, { useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import { mediaQueryUp } from 'components/utils/breakpoint'
import Breakpoint from 'components/Common/Breakpoint'
import Image from 'components/Common/Image'
import LayoutContainer from 'components/Common/LayoutContainer'
import ShareModal from 'components/Common/Modals/ShareModal'
import { getShareModalConfig } from 'lib/getShareModalConfig'
import OfferBNPLAd from '../OfferBNPLAd'
import BundleAndSaveUSPs from './BundleAndSaveUSPs'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import TextButton from 'components/Luxkit/Button/TextButton'
import OfferBadgeTag from 'components/Common/OfferBadgeTag'
import LineMapMarkerIcon from 'components/Luxkit/Icons/line/LineMapMarkerIcon'
import Heading from 'components/Luxkit/Typography/Heading'
import Subtitle from 'components/Luxkit/Typography/Subtitle'
import ShareButton from 'components/Common/ShareWithFriendsButton'
import InlineGroup from 'components/Common/InlineGroup'
import BundleAndSaveSummary from './BundleAndSaveSummary'
import OfferDescription from '../OfferDescription'
import LineEyeIcon from 'components/Luxkit/Icons/line/LineEyeIcon'
import { getOfferExtra, OFFER_VIEWS_24_HOURS_THRESHOLD } from 'selectors/offerExtraSelector'

import { useAppDispatch } from 'hooks/reduxHooks'
import { fetchOfferExtraById } from 'actions/OfferActions'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import OfferUrgencyLabels from 'components/Common/Labels/OfferUrgencyLabels'
import config from 'constants/config'
import { shouldShowSalesUSP } from 'selectors/featuresSelectors'
import ContactUSP from '../ContactUSP'
import SolidPlayIcon from 'components/Luxkit/Icons/solid/SolidPlayIcon'
import LinePlusIcon from 'components/Luxkit/Icons/line/LinePlusIcon'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import { nonNullable } from 'lib/arrayUtils'
import Group from 'components/utils/Group'
import { LUX_BUNDLE_LABEL } from 'constants/productLabel'

const OfferIntroGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${rem(12)};

  ${mediaQueryUp.sm} {
    grid-template-columns: 1fr min-content;
    grid-gap: ${rem(16)} ${rem(48)};
  }
`

const ContentColumn = styled.div`
  > * + * {
    margin-top: ${rem(16)};
  }
`

const OfferBadgeTagSection = styled.div`
  > * + * {
    margin-left: ${rem(8)};
  }
`

const SummaryColumn = styled.div`
  display: none;

  ${mediaQueryUp.sm} {
    display: flex;
    flex-direction: column;
  }

  > * + * {
    margin-top: ${rem(12)};
  }
`

const SummaryBlock = styled.div`
  ${mediaQueryUp.sm} {
    position: sticky;
    top: ${rem(12)};
  }

  > * + * {
    margin-top: ${rem(12)};
  }
`

const HotelSummary = styled(BundleAndSaveSummary)`
  min-width: ${rem(308)};

  ${mediaQueryUp.md} {
    min-width: ${rem(382)};
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const PropertyLogo = styled(Image)`
  width: ${rem(80)};
`

const OfferIntroWrapper = styled(LayoutContainer)`
  padding-top: ${rem(20)};
  padding-bottom: ${rem(20)};

  ${mediaQueryUp.md} {
    padding-top: ${rem(28)};
    padding-bottom: ${rem(28)};
  }
`

const OfferViews = styled.div`
  display: flex;
  padding: ${rem(8)};
  justify-content: center;
  color: ${props => props.theme.palette.messaging.fast.main};
  background-color: ${props => props.theme.palette.messaging.fast.light};
`

const MobileOfferViews = styled(OfferViews)`
  position: relative;
  top: -${rem(20)};
  margin: 0 -${rem(20)};
`

interface Props {
  offer: App.BundleOffer;
  customerPortalURL: string;
  regionCode: string;
  pathname: string;
  memberId: string;
  onWatchVideo: () => void;
  className?: string;
  onViewPackageOptions: () => void;
  offerExtra: App.OfferExtra;
  shouldShowSalesPhoneUSP: boolean;
}

function BundleAndSaveIntro(props: Props) {
  const {
    offer,
    onWatchVideo,
    memberId,
    pathname,
    regionCode,
    customerPortalURL,
    className,
    onViewPackageOptions,
    offerExtra,
    shouldShowSalesPhoneUSP,
  } = props

  const bundledOffers = useMemo(() => Object.values(offer.bundledOffers), [offer.bundledOffers])

  // Eg. `Melbourne`, `Victoria` & `Kuta`, `Bali`
  const locationCaption = useMemo(() =>
    bundledOffers.map(offer =>
      nonNullable([
        offer.locationHeading,
        offer.locationSubheading,
      ]).join(', '))
      .join(' & ')
  , [bundledOffers])

  const offerPropertiesTitle = useMemo(
    () => offer.location || bundledOffers.map(offer => offer.property.name).join(' + ')
    , [offer.location, bundledOffers])

  const shareConfig = getShareModalConfig({
    customerPortalURL,
    image: offer.images[0],
    memberId,
    offerId: offer.id,
    offerLocation: locationCaption,
    pathname,
    region: regionCode,
    type: 'offer',
    utmCampaign: 'webshare',
  })
  const allPrices = useMemo(() => offer.packages.map(p => p.price), [offer.packages])

  const showBadge = !!offer.badge && config.BRAND === 'luxuryescapes'

  const urgencyToday = useMemo(() => {
    if (offerExtra) {
      return {
        isActive: offerExtra.viewsInLast24Hours >= OFFER_VIEWS_24_HOURS_THRESHOLD,
        captionText: `${offerExtra.viewsInLast24Hours} people viewed this offer today`,
        Icon: LineEyeIcon,
      }
    }
    return { isActive: false }
  }, [offerExtra])

  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(fetchOfferExtraById(offer.id))
  }, [dispatch, offer.id])

  return (
    <OfferIntroWrapper>
      { urgencyToday.isActive && <Breakpoint max="xs">
        <MobileOfferViews>
          <CaptionTextBlock variant="medium" weight="bold" startIcon={<urgencyToday.Icon />}>
            {urgencyToday.captionText}
          </CaptionTextBlock>
        </MobileOfferViews>
      </Breakpoint> }
      <OfferIntroGrid className={className} data-testid="offer-intro">
        <ContentColumn>
          <InlineGroup wraps horizontal="between" spacing="sm" vertical="center">
            <LabelGroup>
              <OfferUrgencyLabels offer={offer} />
            </LabelGroup>
            <CSSBreakpoint max="xs" >
              <ShareModal shareConfig={shareConfig}>
                <ShareButton outdent="right" />
              </ShareModal>
            </CSSBreakpoint>
          </InlineGroup>
          <Header>
            <Group direction="vertical" gap={8}>
              <BodyTextBlock variant="medium" startIcon={<LineMapMarkerIcon />} data-testid="offer-location" >
                {locationCaption}
              </BodyTextBlock>
              <Heading variant="heading2" as="h1">
                <Heading variant="heading2" as="b" colour="highlight">{LUX_BUNDLE_LABEL}:</Heading>{' '}
                {offerPropertiesTitle}
              </Heading>
              <Subtitle variant="subtitle2" as="h2">{offer.name}</Subtitle>
            </Group>
            <CSSBreakpoint min="md">
              <Group direction="horizontal" verticalAlign="center" gap={16}>
                <PropertyLogo
                dpr={2}
                enhancedFormats={false}
                id={bundledOffers[0].property.logoImageId}
                alt={`${bundledOffers[0].property.name || 'Vendor'} logo`}
              />
                <LinePlusIcon/>
                <PropertyLogo
                dpr={2}
                enhancedFormats={false}
                id={bundledOffers[1].property.logoImageId}
                alt={`${bundledOffers[1].property.name || 'Vendor'} logo`}
              />
              </Group>
            </CSSBreakpoint>
          </Header>
          {(!!offer.badge || offer.bundledWithFlightsOnly) && <OfferBadgeTagSection>
            {showBadge && <OfferBadgeTag badge={offer.badge} />}
          </OfferBadgeTagSection>}

          {offer.vimeoVideoId &&
            <TextButton kind="secondary" startIcon={<SolidPlayIcon />} onClick={onWatchVideo}>
              Play video
            </TextButton>
        }
          {offer.description && <OfferDescription description={offer.description} />}
          <BundleAndSaveUSPs offer={offer} />
        </ContentColumn>
        <SummaryColumn>
          <CSSBreakpoint min="sm">
            <InlineGroup vertical="center" spacing="sm" horizontal="end">
              <ShareModal shareConfig={shareConfig}>
                <ShareButton outdent="right" />
              </ShareModal>
            </InlineGroup>
          </CSSBreakpoint>
          <SummaryBlock>
            <div>
              { urgencyToday.isActive && <OfferViews>
                <CaptionTextBlock variant="medium" weight="bold" startIcon={<urgencyToday.Icon />}>
                  {urgencyToday.captionText}
                </CaptionTextBlock>
              </OfferViews> }
              {offer.packages.length > 0 && (
                <HotelSummary
                onViewPackageOptions={onViewPackageOptions}
              />
              )}
            </div>
            <OfferBNPLAd prices={allPrices} />
          </SummaryBlock>
        </SummaryColumn>
        {shouldShowSalesPhoneUSP && <ContactUSP />}
      </OfferIntroGrid>
    </OfferIntroWrapper>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  shouldShowSalesPhoneUSP: shouldShowSalesUSP(appState, state.offer?.lowestPricePackage?.trackingPrice, 1000),
  offer: state.offer,
  regionCode: appState.geo.currentRegionCode,
  pathname: appState.router.location.pathname,
  customerPortalURL: appState.config.customerPortalURL,
  memberId: appState.auth.account.memberId,
  offerExtra: getOfferExtra(appState, state.offer?.id),
}))(BundleAndSaveIntro)
